import { useEffect, useRef, useState } from "react";
import styles from "./IsbnScanner.module.css";

export default function IsbnScanner({ onScan, onClose }) {
  const [error, setError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const startScanning = async () => {
    try {
      // Check if BarcodeDetector is supported
      if (!("BarcodeDetector" in window)) {
        throw new Error("Barcode scanning is not supported in this browser");
      }

      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });

      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();

      const barcodeDetector = new BarcodeDetector({
        formats: ["ean_13"],
      });

      // Start detection loop
      const detectBarcode = async () => {
        if (!videoRef.current || !streamRef.current) return;

        try {
          const barcodes = await barcodeDetector.detect(videoRef.current);
          for (const barcode of barcodes) {
            if (barcode.rawValue.length === 13) {
              onScan(barcode.rawValue);
              stopStream();
              onClose();
              return;
            }
          }
          // Continue scanning if no valid barcode found
          requestAnimationFrame(detectBarcode);
        } catch {
          // Ignore detection errors and continue scanning
          requestAnimationFrame(detectBarcode);
        }
      };

      requestAnimationFrame(detectBarcode);
      setIsInitializing(false);
    } catch (err) {
      console.error("Scanner initialization error:", err);
      setError(err.message || "Unable to start camera. Please try again.");
      setIsInitializing(false);
      stopStream();
    }
  };
  useEffect(() => {
    startScanning();
    return () => {
      stopStream();
    };
  }, [onScan, onClose]);

  const handleRetry = () => {
    setError(null);
    setIsInitializing(true);
    startScanning();
  };

  return (
    <div className={styles.scannerContainer}>
      <div className={styles.scannerHeader}>
        <h3>Scan ISBN Barcode</h3>
        <button onClick={onClose} className={styles.closeButton}>
          ×
        </button>
      </div>
      <div className={styles.reader}>
        <video ref={videoRef} className={styles.video} playsInline muted />
        {isInitializing && (
          <div className={styles.initializing}>Initializing camera...</div>
        )}
      </div>
      {error ? (
        <div className={styles.error}>
          <p>{error}</p>
          <button onClick={handleRetry} className={styles.retryButton}>
            Retry
          </button>
        </div>
      ) : (
        <div className={styles.guide}>
          <p>Point your camera at a book's ISBN barcode</p>
          <div className={styles.scanArea}></div>
        </div>
      )}
    </div>
  );
}
